import React, { useState, useEffect, useCallback } from "react";
import {
  getGroupByIdApi,
  addGroupMemberApi,
  removeGroupMemberApi,
  updateGroupApi,
  getUsersApi,
  getPresignedUploadUrlApi,
} from "../../../../api/api";

// ─── Constants ────────────────────────────────────────────────────────────────
const ROOM_TABS = ["Overview", "Members", "Settings"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Active: { bg: "#D1FAE5", color: "#065F46", dot: "#10B981" },
    Inactive: { bg: "#FEF3C7", color: "#92400E", dot: "#F59E0B" },
    Archived: { bg: "#F3F4F6", color: "#6B7280", dot: "#9CA3AF" },
    ADMIN: { bg: "#EFF6FF", color: "#1D4ED8", dot: "#3B82F6" },
    MEMBER: { bg: "#F3F4F6", color: "#374151", dot: "#9CA3AF" },
  };
  const s = map[status] || map.Archived;
  return (
    <span className="status-badge" style={{ background: s.bg, color: s.color }}>
      <span className="dot" style={{ background: s.dot }} />
      {status}
    </span>
  );
};

const Avatar = ({ name = "?", image, size = 32 }) => {
  if (image) {
    return (
      <img
        src={image}
        alt=""
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
        }}
      />
    );
  }
  const initials = name
    .split(" ")
    .slice(-2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const hue = (name.charCodeAt(0) * 5) % 360;
  return (
    <span
      style={{
        width: size,
        height: size,
        fontSize: size * 0.35,
        background: `hsl(${hue},55%,35%)`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        color: "#fff",
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials}
    </span>
  );
};

const Toast = ({ msg, type }) => {
  if (!msg) return null;
  const bg = type === "error" ? "#FEE2E2" : "#D1FAE5";
  const color = type === "error" ? "#991B1B" : "#065F46";
  return (
    <div
      style={{
        padding: "10px 16px",
        background: bg,
        color,
        borderRadius: 8,
        fontSize: 13,
        marginBottom: 12,
      }}
    >
      {type === "error" ? "⚠️" : "✅"} {msg}
    </div>
  );
};

// ─── RoomDetails Component ────────────────────────────────────────────────────
const RoomDetails = ({ room, onBack, onEdit, onArchive, onDelete }) => {
  const [activeTab, setActiveTab] = useState("Overview");
  const [memberSearch, setMemberSearch] = useState("");

  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);

  const [showAddMember, setShowAddMember] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRole, setSelectedRole] = useState("MEMBER");
  const [addingMember, setAddingMember] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const [settingsForm, setSettingsForm] = useState({
    fileLimit: room.fileLimit ?? 50,
    messagePolicy: room.messagePolicy ?? "All members can post",
    retention: room.retention ?? "90 days",
    name: room.name || "",
    description: room.description || "",
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const [groupImage, setGroupImage] = useState(room.groupImage || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const [toast, setToast] = useState({ msg: "", type: "success" });
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "success" }), 3500);
  };

  const fetchMembers = useCallback(async () => {
    setMembersLoading(true);
    setMembersError(null);
    try {
      const res = await getGroupByIdApi(room.id);
      const data = res.data?.group || res.data;
      setMembers(data?.members || []);
    } catch (err) {
      setMembersError(
        err?.response?.data?.message || "Failed to load members.",
      );
      setMembers(room.membersData || []);
    } finally {
      setMembersLoading(false);
    }
  }, [room.id]);

  const fetchAllUsers = useCallback(async () => {
    try {
      const res = await getUsersApi(1, 100);
      setAllUsers(res.data?.users || res.data || []);
    } catch {
      setAllUsers([]);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "Members" && members.length === 0) {
      fetchMembers();
      fetchAllUsers();
    }
  }, [activeTab, fetchMembers, fetchAllUsers]);

  useEffect(() => {
    setGroupImage(room.groupImage || null);
    setSettingsForm({
      fileLimit: room.fileLimit ?? 50,
      messagePolicy: room.messagePolicy ?? "All members can post",
      retention: room.retention ?? "90 days",
      name: room.name || "",
      description: room.description || "",
    });
  }, [room]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await getPresignedUploadUrlApi({
        fileName: file.name,
        mimeType: file.type,
        folder: "profile-images",
      });
      await fetch(data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      setGroupImage(data.fileUrl);
      await updateGroupApi(room.id, { groupImage: data.fileUrl });
      showToast("Image updated successfully.");
    } catch (err) {
      showToast("Failed to upload image.", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) return;
    setAddingMember(true);
    try {
      await addGroupMemberApi(room.id, {
        userId: selectedUserId,
        memberRole: selectedRole,
      });
      showToast("Member added successfully.");
      setShowAddMember(false);
      setSelectedUserId("");
      setSelectedRole("MEMBER");
      fetchMembers();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to add member.",
        "error",
      );
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (idToRemove) => {
    setRemovingId(idToRemove);
    try {
      await removeGroupMemberApi(room.id, idToRemove);
      showToast("Member removed.");
      setMembers((prev) =>
        prev.filter((m) => (m.userId || m.id) !== idToRemove),
      );
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to remove member.",
        "error",
      );
    } finally {
      setRemovingId(null);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await updateGroupApi(room.id, {
        name: settingsForm.name,
        description: settingsForm.description,
        fileLimit: settingsForm.fileLimit,
        messagePolicy: settingsForm.messagePolicy,
        retention: settingsForm.retention,
      });
      showToast("Settings saved successfully.");
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Failed to save settings.",
        "error",
      );
    } finally {
      setSavingSettings(false);
    }
  };

  const filteredMembers = members.filter((m) => {
    const q = memberSearch.toLowerCase();
    const name = (m.fullName || "").toLowerCase();
    return !q || name.includes(q);
  });

  return (
    <div className="room-details">
      <div className="details-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Groups
        </button>

        <div className="details-hero">
          <div style={{ position: "relative" }}>
            {groupImage ? (
              <img
                src={groupImage}
                alt=""
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 16,
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 16,
                  background: "#EFF6FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 32,
                  color: "#0062FE",
                }}
              >
                💬
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              style={{
                position: "absolute",
                bottom: -4,
                right: -4,
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "#0062FE",
                color: "#fff",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              title="Change image"
            >
              {uploading ? "…" : "📷"}
            </button>
          </div>

          <div className="room-hero-info">
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <h2>{room.name}</h2>
              <StatusBadge status={room.status} />
            </div>
            <p>
              👥 {room.members} members • 🏢 {room.department || "—"}
              {/* • 📅{" "}
              {room.createdOn} */}
            </p>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
            <button className="btn-primary" onClick={() => onEdit(room)}>
              Edit
            </button>
            {room.status !== "Archived" && (
              <button
                className="btn-danger-outline"
                onClick={() => onArchive(room)}
              >
                Archive
              </button>
            )}
            <button
              className="btn-danger-outline"
              style={{ color: "#DC2626", borderColor: "#DC2626" }}
              onClick={() => onDelete(room)}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="tab-bar">
        {ROOM_TABS.map((tab) => (
          <button
            key={tab}
            className={`tab-btn${activeTab === tab ? " tab-active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="tab-content">
        <Toast msg={toast.msg} type={toast.type} />

        {activeTab === "Overview" && (
          <div className="overview-grid">
            <div className="info-card">
              <h4>Description</h4>
              <p>{room.description || "No description provided."}</p>
            </div>
            <div className="info-card">
              <h4>Admins</h4>
              {members.filter((m) => m.memberRole === "ADMIN").length > 0 ? (
                members
                  .filter((m) => m.memberRole === "ADMIN")
                  .map((m) => (
                    <div
                      key={m.userId || m.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 10,
                      }}
                    >
                      <Avatar
                        name={m.fullName || m.name}
                        image={m.profileImage}
                      />
                      <span>{m.fullName || m.name}</span>
                    </div>
                  ))
              ) : (
                <p style={{ color: "#9CA3AF", fontSize: 13 }}>
                  No admins assigned.
                </p>
              )}
            </div>
            <div className="info-card">
              <h4>Policies</h4>
              <p>
                Message Policy: <strong>{room.messagePolicy}</strong>
              </p>
              <p>
                File Limit: <strong>{room.fileLimit} MB</strong>
              </p>
              <p>
                Retention: <strong>{room.retention}</strong>
              </p>
            </div>
            {room.matrixRoomId && (
              <div className="info-card">
                <h4>Matrix Info</h4>
                <p
                  style={{
                    fontSize: 12,
                    color: "#6B7280",
                    wordBreak: "break-all",
                  }}
                >
                  Room ID: <strong>{room.matrixRoomId}</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "Members" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 16,
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <input
                className="search-input"
                placeholder="Search members…"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                style={{ flex: 1, minWidth: 200 }}
              />
              <button
                className="btn-primary"
                onClick={() => setShowAddMember(!showAddMember)}
              >
                {showAddMember ? "✕ Cancel" : "+ Add Member"}
              </button>
            </div>

            {showAddMember && (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 20,
                  padding: 16,
                  background: "#F8FAFC",
                  borderRadius: 10,
                  border: "1px solid #E5E7EB",
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                }}
              >
                <div style={{ flex: 2, minWidth: 180 }}>
                  <label
                    style={{
                      fontSize: 12,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    User
                  </label>
                  <select
                    className="form-input"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                  >
                    <option value="">Select a user…</option>
                    {allUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.fullName || u.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 130 }}>
                  <label
                    style={{
                      fontSize: 12,
                      color: "#6B7280",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Role
                  </label>
                  <select
                    className="form-input"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                  >
                    <option value="MEMBER">Member</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>
                <button
                  className="btn-primary"
                  disabled={!selectedUserId || addingMember}
                  onClick={handleAddMember}
                  style={{ alignSelf: "flex-end" }}
                >
                  {addingMember ? "Adding…" : "Add"}
                </button>
              </div>
            )}

            {membersError && (
              <div
                style={{
                  padding: "10px 14px",
                  background: "#FEE2E2",
                  color: "#991B1B",
                  borderRadius: 8,
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                ⚠️ {membersError}{" "}
                <button
                  style={{
                    textDecoration: "underline",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#991B1B",
                  }}
                  onClick={fetchMembers}
                >
                  Retry
                </button>
              </div>
            )}

            {membersLoading ? (
              <div
                style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}
              >
                Loading members…
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th style={{ width: 48 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          textAlign: "center",
                          padding: 32,
                          color: "#9CA3AF",
                        }}
                      >
                        No members found
                      </td>
                    </tr>
                  ) : (
                    filteredMembers.map((m) => {
                      const uid = m.userId || m.id;
                      return (
                        <tr key={uid}>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <Avatar
                                name={m.fullName || m.name}
                                image={m.profileImage}
                              />
                              <div>
                                <div style={{ fontWeight: 500, fontSize: 13 }}>
                                  {m.fullName || m.name}
                                </div>
                                <small style={{ color: "#9CA3AF" }}>
                                  {m.email || m.matrixUserId}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <StatusBadge status={m.memberRole || "MEMBER"} />
                          </td>
                          <td>
                            <StatusBadge status={m.status || "Active"} />
                          </td>
                          <td style={{ fontSize: 13, color: "#6B7280" }}>
                            {m.joinedAt
                              ? new Date(m.joinedAt).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )
                              : "—"}
                          </td>
                          <td>
                            <button
                              className="tbl-action-btn danger"
                              disabled={removingId === uid}
                              onClick={() => handleRemoveMember(uid)}
                              title="Remove member"
                            >
                              {removingId === uid ? "…" : "✕"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === "Settings" && (
          <div style={{ maxWidth: 480 }}>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: 6,
                  fontSize: 13,
                }}
              >
                Group Name
              </label>
              <input
                className="form-input"
                value={settingsForm.name}
                onChange={(e) =>
                  setSettingsForm({ ...settingsForm, name: e.target.value })
                }
              />
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: 6,
                  fontSize: 13,
                }}
              >
                Description
              </label>
              <textarea
                className="form-input"
                rows={3}
                value={settingsForm.description}
                onChange={(e) =>
                  setSettingsForm({
                    ...settingsForm,
                    description: e.target.value,
                  })
                }
                style={{ resize: "vertical" }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: 6,
                  fontSize: 13,
                }}
              >
                File Upload Limit (MB)
              </label>
              <input
                type="number"
                className="form-input"
                min={1}
                max={500}
                value={settingsForm.fileLimit}
                onChange={(e) =>
                  setSettingsForm({
                    ...settingsForm,
                    fileLimit: Number(e.target.value),
                  })
                }
                style={{ width: 140 }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: 6,
                  fontSize: 13,
                }}
              >
                Message Posting Policy
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {["All members can post", "Admins only"].map((p) => (
                  <label
                    key={p}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name="settingsPolicy"
                      checked={settingsForm.messagePolicy === p}
                      onChange={() =>
                        setSettingsForm({ ...settingsForm, messagePolicy: p })
                      }
                    />
                    {p}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: "block",
                  fontWeight: 600,
                  marginBottom: 6,
                  fontSize: 13,
                }}
              >
                Message Retention
              </label>
              <select
                className="form-input"
                value={settingsForm.retention}
                onChange={(e) =>
                  setSettingsForm({
                    ...settingsForm,
                    retention: e.target.value,
                  })
                }
                style={{ width: 200 }}
              >
                {[
                  "30 days",
                  "60 days",
                  "90 days",
                  "180 days",
                  "365 days",
                  "Forever",
                ].map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </div>
            <button
              className="btn-primary"
              onClick={handleSaveSettings}
              disabled={savingSettings}
            >
              {savingSettings ? "Saving…" : "Save Settings"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomDetails;
